import { toast } from "react-toastify";
import Swal from "sweetalert2";

// Track if a 401 popup is already showing to prevent multiple popups
let isShowing401Popup = false;

// Track recent error toasts to prevent spam
let recentErrorToasts = new Set();
const TOAST_DEBOUNCE_TIME = 2000; // 2 seconds

// Track dashboard loading state to handle API cascades
let dashboardErrorCount = 0;
const MAX_DASHBOARD_ERRORS = 3; // Max errors to show before suppressing

// Error message mapping
const errorMessages = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  UNAUTHORIZED:
    "Your session has expired or is active on another device. Please log in again to continue on this device.",
  NOT_FOUND: "Something went wrong.",
  SERVER_ERROR: "Server error occurred.",
  VALIDATION_ERROR: "Please check your input.",
  DEFAULT: "An error occurred. Please try again.",
};

// Error types for consistent handling
export const ErrorTypes = {
  NETWORK_ERROR: "NETWORK_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  NOT_FOUND: "NOT_FOUND",
  SERVER_ERROR: "SERVER_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  DEFAULT: "DEFAULT",
};

// Helper function to show debounced error toast
const showDebouncedErrorToast = (message, errorType) => {
  const toastKey = `${errorType}:${message}`;

  if (recentErrorToasts.has(toastKey)) {
    return; // Skip if this exact error was shown recently
  }

  // Add to recent toasts
  recentErrorToasts.add(toastKey);

  // Remove from recent toasts after debounce time
  setTimeout(() => {
    recentErrorToasts.delete(toastKey);
  }, TOAST_DEBOUNCE_TIME);

  // Show the toast
  Swal.fire("Oops", message, "error");
};

// Helper function to check if we're on dashboard
const isOnDashboard = () => {
  const dashboardPaths = [
    "/dashboard",
    "/",
    "/telecaller",
    "/receptionist",
    "/doctor",
    "/counsellor",
    "/medical",
    "/service-caller",
  ];
  return dashboardPaths.some(
    (path) =>
      window.location.pathname === path ||
      window.location.pathname.startsWith(path)
  );
};

// Function to reset dashboard error count (call this when dashboard loads successfully)
export const resetDashboardErrorCount = () => {
  dashboardErrorCount = 0;
  console.log("Dashboard error count reset");
};

// Function to check if we should suppress errors on dashboard
export const shouldSuppressDashboardError = () => {
  return isOnDashboard() && dashboardErrorCount > MAX_DASHBOARD_ERRORS;
};

// Handle errors consistently across the application
export const handleError = (error, customMessage = null) => {
  console.error("Error occurred:", error);

  let errorType = ErrorTypes.DEFAULT;
  let errorMessage = customMessage || errorMessages.DEFAULT;

  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const serverMessage =
      error.response.data?.messages?.error || error.response.data?.message;

    switch (status) {
      case 400:
        errorType = ErrorTypes.VALIDATION_ERROR;
        errorMessage = serverMessage || errorMessages.VALIDATION_ERROR;
        break;
      case 401:
        errorType = ErrorTypes.UNAUTHORIZED;
        errorMessage = serverMessage || errorMessages.UNAUTHORIZED;

        // Always show 401 popup if not already showing and not on login page
        if (!isShowing401Popup && window.location.pathname !== "/login") {
          isShowing401Popup = true;

          // Show custom popup for 401 errors
          Swal.fire({
            title: "Session Expired",
            text: "Your session has been expired or active on other device. Kindly login again to continue on this device.",
            icon: "warning",
            confirmButtonText: "Login Again",
            confirmButtonColor: "#3085d6",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: false,
          })
            .then((result) => {
              if (result.isConfirmed) {
                // Clear user data and redirect to login
                localStorage.removeItem("userDetails");
                localStorage.removeItem("token");
                window.location.href = "/login";
              }
              // Reset the flag when popup is closed
              isShowing401Popup = false;
            })
            .catch(() => {
              // Reset the flag if there's an error showing the popup
              isShowing401Popup = false;
            });

          // Return early to prevent any other error handling
          return {
            type: errorType,
            message: errorMessage,
          };
        } else {
          // If popup is already showing or on login page, just return without showing anything
          return {
            type: errorType,
            message: errorMessage,
          };
        }
      case 403:
        // Handle 403 (Forbidden) similar to 401 - show login popup
        errorType = ErrorTypes.UNAUTHORIZED;
        errorMessage = "Access denied. Please log in again.";

        if (!isShowing401Popup && window.location.pathname !== "/login") {
          isShowing401Popup = true;

          Swal.fire({
            title: "Access Denied",
            text: errorMessage,
            icon: "warning",
            confirmButtonText: "Login Again",
            confirmButtonColor: "#3085d6",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: false,
          })
            .then((result) => {
              if (result.isConfirmed) {
                localStorage.removeItem("userDetails");
                localStorage.removeItem("token");
                window.location.href = "/login";
              }
              isShowing401Popup = false;
            })
            .catch(() => {
              isShowing401Popup = false;
            });

          return {
            type: errorType,
            message: errorMessage,
          };
        } else {
          return {
            type: errorType,
            message: errorMessage,
          };
        }
      case 404:
        errorType = ErrorTypes.NOT_FOUND;
        errorMessage = serverMessage || errorMessages.NOT_FOUND;
        break;
      case 500:
        errorType = ErrorTypes.SERVER_ERROR;
        errorMessage = serverMessage || errorMessages.SERVER_ERROR;
        break;
      default:
        errorMessage = serverMessage || errorMessages.DEFAULT;
    }
  } else if (error.request) {
    // Request made but no response received
    errorType = ErrorTypes.NETWORK_ERROR;
    errorMessage = errorMessages.NETWORK_ERROR;
  }

  // If 401 popup is already showing, suppress ALL toasts to prevent spam
  if (isShowing401Popup) {
    console.log(
      `Suppressing error toast while 401 popup is active: ${errorMessage}`
    );
    return {
      type: errorType,
      message: errorMessage,
    };
  }

  // For dashboard, suppress most error toasts and prioritize login popup
  if (isOnDashboard()) {
    dashboardErrorCount++;

    // If we're on dashboard and have authentication issues, prioritize login popup
    if (
      errorType === ErrorTypes.UNAUTHORIZED ||
      errorType === ErrorTypes.NETWORK_ERROR
    ) {
      // Don't show toasts for auth/network errors on dashboard
      return {
        type: errorType,
        message: errorMessage,
      };
    }

    // For other errors on dashboard, limit the number of toasts
    if (dashboardErrorCount > MAX_DASHBOARD_ERRORS) {
      console.log(
        `Suppressing dashboard error #${dashboardErrorCount}: ${errorMessage}`
      );
      return {
        type: errorType,
        message: errorMessage,
      };
    }

    // Reset error count after a delay
    setTimeout(() => {
      dashboardErrorCount = Math.max(0, dashboardErrorCount - 1);
    }, 5000);
  }

  // Show error toast only for non-auth errors and when not on dashboard with auth issues
  if (errorType !== ErrorTypes.UNAUTHORIZED) {
    showDebouncedErrorToast(errorMessage, errorType);
  }

  return {
    type: errorType,
    message: errorMessage,
  };
};

// Handle success messages consistently
export const handleSuccess = (message) => {
  toast.success(message, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

export const showErrorAlert = (message) => {
  // If 401 popup is already showing, suppress all error toasts
  if (isShowing401Popup) {
    console.log(
      `Suppressing error alert while 401 popup is active: ${message}`
    );
    return;
  }

  // For dashboard, check if we should suppress based on error count
  if (isOnDashboard()) {
    dashboardErrorCount++;

    if (dashboardErrorCount > MAX_DASHBOARD_ERRORS) {
      console.log(
        `Suppressing dashboard error alert #${dashboardErrorCount}: ${message}`
      );
      return;
    }

    // Reset error count after a delay
    setTimeout(() => {
      dashboardErrorCount = Math.max(0, dashboardErrorCount - 1);
    }, 5000);
  }

  toast.error(message, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

export const showInfo = (message) => {
  toast.info(message, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};
